import type { Dayjs } from 'dayjs';

import type { UppgiftsTyp, Myndighetsuppgift } from './myndighetsdatum';

// ----------------------------------------------------------------------

export type AttGöraKälla = 'MYNDIGHET' | 'FAKTURA';

/** En rad i att göra-listan, oavsett var den kommer ifrån. */
export type AttGöraPost = {
  id: string;
  titel: string;
  detalj?: string;
  källa: AttGöraKälla;
  typ?: UppgiftsTyp;
  avsändare: string;
  förfallodag: Dayjs;
  lagrum?: string;
  belopp?: number;
  länk?: string;
};

/** 'INGEN' = organisationen redovisar ingen moms. undefined = använd organisationens registrerade värde. */
export type MomsperiodVal = 'INGEN' | 'MÅNAD' | 'KVARTAL' | 'ÅRLIG';

export const MOMSPERIOD_ETIKETT: Record<MomsperiodVal, string> = {
  INGEN: 'Ingen',
  MÅNAD: 'Månad',
  KVARTAL: 'Kvartal',
  ÅRLIG: 'Helår',
};

export type AttGöraInställningar = {
  /**
   * Användarens uttryckliga val. undefined = härled ur organisationen
   * (`bedömArbetsgivare`). Fältet hette tidigare `ärArbetsgivare` och var alltid
   * `true` som standard; namnbytet gör att gamla lagrade värden ignoreras i
   * stället för att fortsätta tvinga fram arbetsgivardeklarationer.
   */
  ärArbetsgivareVal?: boolean;
  harDebiteradPreliminärskatt: boolean;
  lämnarDigitalt: boolean;
  /**
   * Lokal override av organisationens momsredovisningsperiod. Många
   * organisationer saknar värdet i registret, och utan det kan momsdatumen
   * inte räknas ut alls.
   */
  momsperiod?: MomsperiodVal;
  /** Uppgiftstyper användaren valt att dölja. */
  dolda: UppgiftsTyp[];
};

export const STANDARDINSTÄLLNINGAR: AttGöraInställningar = {
  ärArbetsgivareVal: undefined,
  harDebiteradPreliminärskatt: false,
  lämnarDigitalt: true,
  momsperiod: undefined,
  dolda: [],
};

export const UPPGIFTSTYP_ETIKETT: Record<UppgiftsTyp, string> = {
  ARBETSGIVARDEKLARATION: 'Arbetsgivardeklaration',
  MOMSDEKLARATION: 'Momsdeklaration',
  PRELIMINÄRSKATT: 'Preliminärskatt',
  INKOMSTDEKLARATION: 'Inkomstdeklaration',
  ÅRSREDOVISNING: 'Årsredovisning',
  KONTROLLUPPGIFT: 'Kontrolluppgift',
  STÄMMA: 'Stämma',
};

const MYNDIGHETSLÄNK: Record<string, string> = {
  Skatteverket: 'https://www.skatteverket.se/foretag',
  Bolagsverket: 'https://bolagsverket.se/',
};

export function tillAttGöraPost(uppgift: Myndighetsuppgift): AttGöraPost {
  return {
    id: uppgift.id,
    titel: uppgift.titel,
    detalj: uppgift.detalj,
    källa: 'MYNDIGHET',
    typ: uppgift.typ,
    avsändare: uppgift.myndighet,
    förfallodag: uppgift.förfallodag,
    lagrum: uppgift.lagrum,
    länk: MYNDIGHETSLÄNK[uppgift.myndighet],
  };
}

// ----------------------------------------------------------------------

export type Brådska = 'försenad' | 'idag' | 'snart' | 'kommande';

export function brådska(förfallodag: Dayjs, idag: Dayjs): Brådska {
  const dagarKvar = förfallodag.startOf('day').diff(idag.startOf('day'), 'day');
  if (dagarKvar < 0) return 'försenad';
  if (dagarKvar === 0) return 'idag';
  if (dagarKvar <= 14) return 'snart';
  return 'kommande';
}

export const BRÅDSKA_FÄRG: Record<Brådska, 'error' | 'warning' | 'info' | 'default'> = {
  försenad: 'error',
  idag: 'error',
  snart: 'warning',
  kommande: 'default',
};

export function beskrivDagarKvar(förfallodag: Dayjs, idag: Dayjs): string {
  const dagarKvar = förfallodag.startOf('day').diff(idag.startOf('day'), 'day');
  if (dagarKvar < -1) return `${Math.abs(dagarKvar)} dagar sen`;
  if (dagarKvar === -1) return '1 dag sen';
  if (dagarKvar === 0) return 'Idag';
  if (dagarKvar === 1) return 'Imorgon';
  return `${dagarKvar} dagar kvar`;
}
